<template>
    <div class="card card-info">
        <div class="card-header">
            <h3 class="card-title">
                <i class="fas fa-bolt"></i> Quick Run
            </h3>
            <div class="card-tools">
                <button type="button" class="btn btn-tool" data-card-widget="collapse">
                    <i class="fas fa-minus"></i>
                </button>
            </div>
        </div>

        <form @submit.prevent="runNow">
            <div class="card-body">
                <p class="text-muted">Run an operation immediately without creating a schedule.</p>
                
                <div class="row">
                    <div class="col-md-2">
                        <div class="form-group">
                            <label>Operation</label>
                            <select class="form-control form-control-sm" v-model="operation_type">
                                <option value="request-statistics">Request Statistics</option>
                                <option value="recalculate">Recalculate</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="form-group">
                            <label>Target Type</label>
                            <select class="form-control form-control-sm" v-model="target_type" @change="onTargetTypeChange">
                                <option value="websites">All Websites</option>
                                <option value="website">Website</option>
                                <option value="group">Group</option>
                                <option value="data">Data</option>
                                <option value="supplier">Supplier</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="form-group">
                            <label>Select Target</label>
                            <div v-if="target_type === 'websites'">
                                <span class="badge badge-info mt-2">All (ID: 0)</span>
                            </div>
                            <div v-else class="position-relative">
                                <div class="input-group input-group-sm">
                                    <input 
                                        type="text" 
                                        class="form-control" 
                                        v-model="searchQuery"
                                        :placeholder="'Search ' + target_type + '...'"
                                        @input="debounceSearch"
                                        @focus="showDropdown = true"
                                    >
                                    <div class="input-group-append">
                                        <button 
                                            class="btn btn-outline-secondary" 
                                            type="button"
                                            @click="loadTargets"
                                            :disabled="loadingTargets"
                                        >
                                            <i class="fas" :class="loadingTargets ? 'fa-spinner fa-spin' : 'fa-search'"></i>
                                        </button>
                                    </div>
                                </div>
                                
                                <!-- Dropdown Results -->
                                <div class="target-dropdown" v-show="showDropdown && targets.length > 0">
                                    <div 
                                        class="target-item"
                                        v-for="target in targets" 
                                        :key="target.id"
                                        @click.stop="toggleTarget(target)"
                                        :class="{ 'selected': isTargetSelected(target.id) }"
                                    >
                                        <i class="far mr-2" :class="isTargetSelected(target.id) ? 'fa-check-square' : 'fa-square'"></i>
                                        <strong>#{{ target.id }}</strong> - {{ target.name || target.domain || target.label }}
                                    </div>
                                </div>
                                
                                <div v-if="selectedTargets.length > 0" class="mt-1">
                                    <span class="badge badge-success mr-1 mb-1" v-for="target in selectedTargets" :key="target.id">
                                        #{{ target.id }} - {{ target.name || target.domain || target.label }}
                                        <button type="button" class="btn-clear" @click="removeTarget(target.id)">×</button>
                                    </span>
                                    <button type="button" class="btn btn-xs btn-outline-danger" @click="clearAllTargets">Clear All</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="form-group">
                            <label>Date</label>
                            <select class="form-control form-control-sm" v-model="dateMode">
                                <option value="yesterday">Yesterday</option>
                                <option value="today">Today</option>
                                <option value="hourly">Hourly</option>
                                <option value="specific">Select Date</option>
                                <option value="range">Date Range</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-md-2" v-if="dateMode === 'specific'">
                        <div class="form-group">
                            <label>Pick Date</label>
                            <input 
                                type="date" 
                                class="form-control form-control-sm" 
                                v-model="specificDate"
                                :max="maxDate"
                            >
                        </div>
                    </div>
                    <div class="col-md-2" v-if="dateMode === 'range'">
                        <div class="form-group">
                            <label>Start Date</label>
                            <input
                                type="date"
                                class="form-control form-control-sm"
                                v-model="startDate"
                                :max="maxDate"
                            >
                        </div>
                    </div>
                    <div class="col-md-2" v-if="dateMode === 'range'">
                        <div class="form-group">
                            <label>End Date</label>
                            <input
                                type="date"
                                class="form-control form-control-sm"
                                v-model="endDate"
                                :max="maxDate"
                            >
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="form-group">
                            <label>&nbsp;</label>
                            <button type="submit" class="btn btn-info btn-sm btn-block" :disabled="running">
                                <i class="fas fa-play" :class="{'fa-spin': running}"></i>
                                {{ running ? 'Running...' : 'Run Now' }}
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Result panel -->
                <div v-if="result" class="mt-3">
                    <div class="alert" :class="result.success ? 'alert-success' : 'alert-danger'">
                        <h5>
                            <i :class="result.success ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
                            {{ result.success ? 'Success' : 'Failed' }}
                        </h5>
                        <div class="mb-2">
                            <span class="badge mr-1" :class="result.success ? 'badge-success' : 'badge-danger'">
                                {{ result.statusText }}
                            </span>
                            <span class="badge badge-secondary mr-1">Total: {{ result.total }}</span>
                            <span class="badge badge-success mr-1">Success: {{ result.successCount }}</span>
                            <span class="badge badge-danger">Failed: {{ result.failedCount }}</span>
                            <small class="d-block mt-1 text-dark">Finished: {{ result.finishedAt }}</small>
                        </div>

                        <div v-if="result.items && result.items.length > 0" class="mb-2">
                            <div
                                v-for="item in result.items"
                                :key="`target-${item.targetId}-${item.date || 'n/a'}`"
                                class="d-flex justify-content-between align-items-center py-1 border-bottom"
                            >
                                <span>Target #{{ item.targetId }}<span v-if="item.date"> on {{ item.date }}</span></span>
                                <span class="badge" :class="item.exitCode === 0 ? 'badge-success' : 'badge-danger'">
                                    {{ item.exitCode === 0 ? 'Success' : 'Failed' }}
                                </span>
                            </div>
                        </div>

                        <p class="mb-1"><strong>Command:</strong></p>
                        <pre class="p-2 mb-2 bg-white border rounded text-dark command-box">{{ result.command || 'No command returned' }}</pre>
                        <details v-if="result.output">
                            <summary>View Output</summary>
                            <pre class="mt-2 p-2 bg-dark text-light" style="max-height: 200px; overflow: auto;">{{ result.output }}</pre>
                        </details>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import { RepositoryFactory } from '@/services/repositories/ManagementAPI/RepositoryFactory'
const SchedulerRepo = RepositoryFactory.get('scheduler')
const WebsitesRepo = RepositoryFactory.get('websites')
const GroupsRepo = RepositoryFactory.get('groups')
const GroupsDataRepo = RepositoryFactory.get('groups_data')
const SuppliersRepo = RepositoryFactory.get('suppliers')

export default {
    name: 'RunNow',
    data() {
        return {
            operation_type: 'request-statistics',
            target_type: 'websites',
            target_ids: [],
            dateMode: 'yesterday',
            specificDate: '',
            startDate: '',
            endDate: '',
            running: false,
            result: null,
            
            // Target selection
            targets: [],
            searchQuery: '',
            showDropdown: false,
            loadingTargets: false,
            searchTimeout: null,
            selectedTargets: []
        }
    },
    computed: {
        maxDate() {
            return new Date().toISOString().split('T')[0]
        }
    },
    watch: {
        target_type(newVal) {
            if (newVal === 'websites') {
                this.target_ids = []
                this.selectedTargets = []
            }
        },
        dateMode(newVal) {
            if (newVal === 'specific' && !this.specificDate) {
                // Default to yesterday's date
                const yesterday = new Date()
                yesterday.setDate(yesterday.getDate() - 1)
                this.specificDate = yesterday.toISOString().split('T')[0]
            } else if (newVal === 'range') {
                if (!this.startDate || !this.endDate) {
                    const yesterday = new Date()
                    yesterday.setDate(yesterday.getDate() - 1)
                    const formatted = yesterday.toISOString().split('T')[0]
                    this.startDate = formatted
                    this.endDate = formatted
                }
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        onTargetTypeChange() {
            this.target_ids = []
            this.selectedTargets = []
            this.targets = []
            this.searchQuery = ''
            
            if (this.target_type !== 'websites') {
                this.loadTargets()
            }
        },
        debounceSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.loadTargets()
            }, 300)
        },
        async loadTargets() {
            if (this.target_type === 'websites') return
            
            this.loadingTargets = true
            this.showDropdown = true
            
            try {
                let response
                const search = this.searchQuery
                
                switch (this.target_type) {
                    case 'website':
                        response = await WebsitesRepo.page(1, 30, search)
                        this.targets = response.data.results.map(w => ({
                            id: w.id,
                            name: w.name || w.url
                        }))
                        break
                        
                    case 'group':
                        response = await GroupsRepo.page(0, 1, 30, search)
                        this.targets = response.data.results.map(g => ({
                            id: g.id,
                            name: g.name || `Group ${g.id}`
                        }))
                        break
                        
                    case 'data':
                        response = await GroupsDataRepo.page(0, 1, 30, search)
                        this.targets = response.data.results.map(d => ({
                            id: d.id,
                            name: d.name || d.label || `Data ${d.id}`
                        }))
                        break
                        
                    case 'supplier':
                        response = await SuppliersRepo.query({ search, limit: 30, page: 1 })
                        this.targets = response.data.results.map(s => ({
                            id: s.id,
                            name: s.name
                        }))
                        break
                }
            } catch (error) {
                console.error('Failed to load targets:', error)
                this.targets = []
            } finally {
                this.loadingTargets = false
            }
        },
        isTargetSelected(targetId) {
            return this.target_ids.includes(targetId)
        },
        toggleTarget(target) {
            const targetId = target.id
            const selected = this.target_ids.includes(targetId)
            if (selected) {
                this.target_ids = this.target_ids.filter(id => id !== targetId)
                this.selectedTargets = this.selectedTargets.filter(t => t.id !== targetId)
            } else {
                this.target_ids = [...this.target_ids, targetId]
                this.selectedTargets = [...this.selectedTargets, target]
            }
        },
        removeTarget(targetId) {
            this.target_ids = this.target_ids.filter(id => id !== targetId)
            this.selectedTargets = this.selectedTargets.filter(t => t.id !== targetId)
        },
        clearAllTargets() {
            this.target_ids = []
            this.selectedTargets = []
        },
        handleClickOutside(event) {
            const dropdown = this.$el.querySelector('.target-dropdown')
            const input = this.$el.querySelector('.input-group')
            if (dropdown && input && !dropdown.contains(event.target) && !input.contains(event.target)) {
                this.showDropdown = false
            }
        },
        async runNow() {
            this.running = true
            this.result = null
            
            try {
                if (this.dateMode === 'specific' && !this.specificDate) {
                    throw new Error('Please select a specific date')
                }
                if (this.dateMode === 'range') {
                    if (!this.startDate || !this.endDate) {
                        throw new Error('Please select start and end dates')
                    }
                    if (this.startDate > this.endDate) {
                        throw new Error('Start date must be before or equal to end date')
                    }
                }

                const idsToRun = this.target_type === 'websites'
                    ? [0]
                    : (this.target_ids.length > 0 ? this.target_ids : [0])

                const responses = []
                for (const targetId of idsToRun) {
                    const payload = {
                        operation_type: this.operation_type,
                        target_type: this.target_type,
                        target_id: targetId,
                        date: this.dateMode === 'specific' ? this.specificDate : (this.dateMode === 'range' ? 'yesterday' : this.dateMode)
                    }

                    if (this.dateMode === 'specific') {
                        payload.date_mode = 'specific'
                        payload.specific_date = this.specificDate
                    } else if (this.dateMode === 'range') {
                        payload.date_mode = 'range'
                        payload.start_date = this.startDate
                        payload.end_date = this.endDate
                    }

                    const response = await SchedulerRepo.runNow({
                        ...payload
                    })
                    const responsePayload = response && response.data ? response.data : {}

                    if (Array.isArray(responsePayload.results) && responsePayload.results.length > 0) {
                        for (const item of responsePayload.results) {
                            responses.push({
                                target_id: targetId,
                                date: item.date || null,
                                exit_code: typeof item.exit_code === 'number' ? item.exit_code : 1,
                                command: item.command || '',
                                output: item.output || responsePayload.message || 'Empty response body from server'
                            })
                        }
                    } else {
                        responses.push({
                            target_id: targetId,
                            date: null,
                            exit_code: typeof responsePayload.exit_code === 'number' ? responsePayload.exit_code : 1,
                            command: responsePayload.command || '',
                            output: responsePayload.output || responsePayload.message || 'Empty response body from server'
                        })
                    }
                }

                const allSuccess = responses.every(r => r.exit_code === 0)
                const hasAnySuccess = responses.some(r => r.exit_code === 0)

                this.result = {
                    success: allSuccess,
                    statusText: allSuccess ? 'Completed' : (hasAnySuccess ? 'Partially Completed' : 'Failed'),
                    total: responses.length,
                    successCount: responses.filter(r => r.exit_code === 0).length,
                    failedCount: responses.filter(r => r.exit_code !== 0).length,
                    finishedAt: new Date().toLocaleString(),
                    items: responses.map((r) => ({
                        targetId: r.target_id,
                        date: r.date,
                        exitCode: r.exit_code
                    })),
                    command: responses.map(r => r.command).join('\n'),
                    output: responses.map((r) => {
                        const dateLabel = r.date ? ` (${r.date})` : ''
                        return `Target ${r.target_id}${dateLabel}:\n${r.output || ''}`
                    }).join('\n\n')
                }

                if (allSuccess) {
                    this.notifyToast('success', 'Operation completed successfully')
                } else if (hasAnySuccess) {
                    this.notifyToast('warning', 'Operation completed partially')
                } else {
                    this.notifyToast('warning', 'Operation completed with errors')
                }

                this.$emit('refresh')
            } catch (error) {
                this.result = {
                    success: false,
                    statusText: 'Failed',
                    total: 0,
                    successCount: 0,
                    failedCount: 1,
                    finishedAt: new Date().toLocaleString(),
                    items: [],
                    command: '',
                    output: error?.response?.data?.detail?.message || error?.response?.data?.message || error?.message || 'Unknown error'
                }
                this.notifyToast('error', 'Operation failed')
                console.error(error)
            } finally {
                this.running = false
            }
        },
        notifyToast(type, message) {
            if (this.$toast && typeof this.$toast[type] === 'function') {
                this.$toast[type](message)
            }
        }
    }
}
</script>

<style scoped>
pre {
    font-size: 0.8em;
    white-space: pre-wrap;
    word-break: break-all;
}
.position-relative {
    position: relative;
}
.target-dropdown {
    position: absolute;
    z-index: 1000;
    width: 100%;
    max-height: 200px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #ced4da;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.target-item {
    padding: 6px 10px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
    font-size: 0.9em;
}
.target-item:hover {
    background-color: #f8f9fa;
}
.target-item.selected {
    background-color: #e3f2fd;
}
.target-item:last-child {
    border-bottom: none;
}
.btn-clear {
    background: none;
    border: none;
    color: inherit;
    padding: 0 0 0 5px;
    cursor: pointer;
    font-size: 1.1em;
    line-height: 1;
}
.command-box {
    max-height: 120px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
}
</style>
